import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';
import DateRangePicker, { calcDatePreset } from '../../components/fields/DateRangePicker';
import ShrinkageChartComponent from '../../components/charts/ShrinkageChartComponent';
import QtyDual, { baseToPackageQty, inputUnitLabel } from '../../utils/inventoryUnit';
import { inventoryAPI } from '../../services';

const fmtCurrency = (n) => (n == null ? '—' : `${Number(n).toLocaleString('vi-VN')}đ`);
const fmtPct = (n) => (n == null ? '—' : `${n >= 0 ? '+' : ''}${Number(n).toFixed(1)}%`);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('vi-VN') : '—');
/**
 * #192444 bổ sung 3 — "chiếm bao nhiêu % tổng hao hụt". Cố ý KHÔNG dùng `fmtPct`: `fmtPct` gắn
 * dấu "+" ở đầu (hợp lý cho mức hao hụt, vô nghĩa cho một tỷ trọng) và đây là con số phụ trợ nên
 * viết ngắn nhất có thể — khung điện thoại ~390px không còn chỗ.
 */
const fmtShare = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);
/**
 * #172026: 3 loại cờ cảnh báo BẰNG CHỮ cho bảng chi tiết mặc định. Cố ý không dùng ký hiệu/màu
 * suông — người xem phải đọc được ngay vì sao con số ở dòng đó chưa dùng để kết luận được.
 */
const WARNING_UNRELIABLE_BASELINE = 'Số liệu chưa tin được — mốc tồn kho bị lỗi gõ đơn vị';
const WARNING_NO_MANUAL_COUNT = 'Chưa có số đếm tay để đối chiếu';
const WARNING_NO_OPENING = 'Kỳ này chưa đo được tồn đầu kỳ';

function materialWarnings(m) {
  const out = [];
  if (m.balance_baseline_unreliable) out.push(WARNING_UNRELIABLE_BASELINE);
  if (m.opening_baseline_missing) out.push(WARNING_NO_OPENING);
  if (!m.shift_count || m.shift_count.status !== 'HAS_COUNT') out.push(WARNING_NO_MANUAL_COUNT);
  return out;
}

/**
 * #192444 bổ sung 1 — cột "Cờ cảnh báo" đã bị gỡ, 3 câu trên nay là DÒNG NHỎ dưới tên NVL. Câu
 * đầy đủ quá dài cho khung điện thoại (~390px) nên rút thành nhãn ngắn, giữ nguyên câu đầy đủ ở
 * `title` để rê chuột/chạm là đọc được. KHÔNG mất thông tin nào.
 */
const WARNING_SHORT = {
  [WARNING_UNRELIABLE_BASELINE]: 'mốc tồn kho lỗi đơn vị',
  [WARNING_NO_MANUAL_COUNT]: 'chưa đếm tay',
  [WARNING_NO_OPENING]: 'chưa đo được tồn đầu kỳ',
};

const BTP_REASON_LABEL = {
  INVALID_YIELD: 'thiếu recipe_yield_quantity (chưa cấu hình) — NVL của bán thành phẩm này KHÔNG được trừ kho',
  NON_RAW_INGREDIENT: 'công thức BTP chứa nguyên liệu không phải NVL thô',
  NO_VALID_INGREDIENTS: 'công thức BTP không có nguyên liệu hợp lệ nào',
};

/**
 * #172543 — số lượng viết bằng ĐÚNG đơn vị cơ sở (G/ML), 3 số lẻ, đúng như backend làm tròn.
 * #172026 bổ sung 3: đơn vị gốc KHÔNG còn hiện mặc định — chỉ dùng trong khối "Số liệu tiền và
 * số liệu gốc" đã gấp lại.
 */
const fmtQtyBase = (n) =>
  Number(n ?? 0).toLocaleString('vi-VN', { maximumFractionDigits: 3 });

/**
 * #172026 bổ sung 3 (lệnh sếp 28/08/2026: "thay đổi cách trình bày để dễ hiểu hơn đi") — số lượng
 * viết bằng ĐƠN VỊ NGƯỜI DÙNG HIỂU (túi / kg / chai…), 1 số lẻ. Cố ý làm tròn TRƯỚC khi cộng trừ
 * để người xem cộng tay lại đúng bằng con số đang hiện; sai số làm tròn tối đa 0,1 đơn vị nằm ở
 * dòng kết luận, không đủ đổi kết luận thiếu/dư.
 */
const roundTo = (n, d) => {
  const f = 10 ** d;
  return Math.round(Number(n ?? 0) * f) / f;
};

/**
 * Chọn số chữ số thập phân đủ để KHÔNG có vế nào khác 0 mà bị làm tròn thành "0" — dòng "− 0 túi"
 * nằm giữa một phép tính thì người đọc tưởng kỳ này không bán gì.
 */
function pickDecimals(values) {
  const nz = values.filter((v) => Math.abs(v) > 0);
  for (const d of [1, 2, 3]) {
    if (nz.every((v) => Math.abs(roundTo(v, d)) > 0)) return d;
  }
  return 3;
}

/** Một dòng của phép tính: nhãn bên trái · đúng MỘT con số bên phải · ghi chú mờ ở sau. */
function EqRow({ label, sign, value, unit, note, bold, fmt }) {
  const num = (
    <>
      {sign ? <span className="me-1">{sign}</span> : null}
      {fmt(value)} {unit}
    </>
  );
  return (
    <tr>
      <td className="ps-0 pe-4 text-nowrap">{bold ? <strong>{label}</strong> : label}</td>
      <td className="text-end text-nowrap font-monospace">{bold ? <strong>{num}</strong> : num}</td>
      <td className="ps-3 text-muted">{note}</td>
    </tr>
  );
}

/** Đường kẻ ngang ngăn "kết quả trung gian" với "kết luận" — 2 đường đúng chỗ theo yêu cầu sếp. */
function EqRule() {
  return (
    <tr>
      <td colSpan={3} className="p-0">
        <hr className="my-1" />
      </td>
    </tr>
  );
}

/**
 * #172026 bổ sung 3 — khối chi tiết của MỘT dòng NVL là MỘT PHÉP TÍNH ĐỌC TỪ TRÊN XUỐNG.
 *
 * Trước đó (#172543) đây là 16 ô nhãn–giá–trị dán cạnh nhau cộng một dòng phép tính viết ngang
 * bằng đơn vị gốc (G/ML). Sếp bắt lỗi 28/08/2026: người đọc không biết số nào cộng với số nào ra
 * số nào nên không tự kiểm được con số hao hụt ở bảng trên. Nay mỗi dòng đúng một con số, dấu +/−
 * hiện rõ ở đầu số, hai đường kẻ ngang tách kết quả trung gian với kết luận, và tất cả viết bằng
 * đơn vị người dùng hiểu.
 *
 * Số liệu KHÔNG đáng tin (mốc tồn bẩn / kỳ chưa đo được tồn đầu kỳ) thì KHÔNG vẽ phép tính như
 * thật — thay bằng đúng một dòng chữ nói vì sao chưa tính được.
 */
function ShrinkageEquation({ m, mat, blockedText }) {
  if (blockedText) {
    return (
      <div className="small text-muted border-start border-3 ps-3 py-1 mb-3">{blockedText}</div>
    );
  }

  const unit = inputUnitLabel(mat) || m.unit || '';
  const raw = [m.opening_qty, m.inflow_qty, m.theoretical_qty, m.transfer_out_qty ?? 0, m.closing_qty]
    .map((v) => baseToPackageQty(v, mat));
  const dec = pickDecimals(raw);
  const [opening, inflow, theoretical, transferOut, closing] = raw.map((v) => roundTo(v, dec));
  const expected = roundTo(opening + inflow - theoretical - transferOut, dec);
  const diff = roundTo(closing - expected, dec); // dương = dư, âm = thiếu
  const isShort = diff < 0;
  const fmt = (n) => Number(n ?? 0).toLocaleString('vi-VN', { maximumFractionDigits: dec });

  return (
    <div className="mb-3">
      <Table borderless size="sm" className="mb-0 w-auto small">
        <tbody>
          <EqRow label="Đầu kỳ có" value={opening} unit={unit} fmt={fmt} />
          <EqRow label="Nhập thêm trong kỳ" sign="+" value={inflow} unit={unit} fmt={fmt} />
          <EqRow
            label="Bán hàng lẽ ra dùng hết"
            sign="−"
            value={theoretical}
            unit={unit}
            note="(theo công thức món đã bán)"
            fmt={fmt}
          />
          {transferOut > 0 && (
            <EqRow
              label="Chuyển sang chi nhánh khác"
              sign="−"
              value={transferOut}
              unit={unit}
              fmt={fmt}
            />
          )}
          <EqRule />
          <EqRow label="Vậy cuối kỳ phải còn" value={expected} unit={unit} fmt={fmt} />
          {/* #192444 bổ sung 2 (YC C) — SỬA CHỮ SAI SỰ THẬT. Nhãn cũ "Thực tế đếm được" khiến
              người đọc tưởng đây là số NHÂN VIÊN ĐẾM TAY, trong khi nó là số dư cuối cùng của SỔ
              KHO (`balance_after` của giao dịch kho cuối kỳ — stock-balance.util.ts:223,240).
              Trên dữ liệu thật, 27/31 NVL của CN Quận Thủ Đức chưa hề bật đếm tay mà vẫn hiện
              dòng này, nên ngay dưới lại có câu "Chưa bật đếm tay cho nguyên vật liệu này" —
              đúng mâu thuẫn sếp bắt được. Con số KHÔNG sai, chỉ cái tên gọi sai. */}
          <EqRow label="Tồn cuối kỳ theo sổ kho" value={closing} unit={unit} fmt={fmt} />
          <EqRule />
          <tr>
            <td colSpan={3} className="ps-0">
              {isShort ? (
                <strong className="text-danger">
                  ⇒ THIẾU {fmt(Math.abs(diff))} {unit}
                  {m.balance_shrinkage_value != null && ` ≈ ${fmtCurrency(m.balance_shrinkage_value)}`}
                  {m.balance_shrinkage_pct_available != null
                    && ` · hao hụt ${fmtPct(m.balance_shrinkage_pct_available)}`}
                </strong>
              ) : (
                <strong className="text-success">
                  ⇒ DƯ {fmt(diff)} {unit} · không hao hụt
                </strong>
              )}
            </td>
          </tr>
        </tbody>
      </Table>
    </div>
  );
}

const btpReasonLabel = (reason) => (
  Object.prototype.hasOwnProperty.call(BTP_REASON_LABEL, reason) ? BTP_REASON_LABEL[reason] : reason
);

const emptyDataQuality = {
  menu_items_without_recipe: [],
  materials_without_price: [],
  btp_data_issues: [],
  base_unit_package_purchases: [],
  suspicious_purchase_pricing: [],
  orders_missing_deduction: { count: 0, sample_order_ids: [] },
  // #153458 (PA2): ca biên của công thức tồn đầu/cuối kỳ.
  backdated_stock_transactions: { count: 0, materials: [] },
  materials_without_opening_baseline: [],
  unit_scale_stock_adjustments: [],
};

// #153458 (PA3): nhãn trạng thái số đếm tay theo ca. CỐ Ý không có trạng thái nào hiển thị ô trống
// — trống trơn dễ bị đọc nhầm thành "hệ thống và người đếm khớp nhau".
const SHIFT_STATUS_LABEL = {
  HAS_COUNT: 'Có số đếm tay',
  FLAGGED_NO_COUNT: 'Có cờ kiểm ca nhưng kỳ này chưa có số đếm tay dùng được',
  NOT_FLAGGED: 'Chưa bật kiểm ca — không có số đếm tay để đối chiếu',
};

/**
 * ON-32: Hao hụt NVL = Tiền mua NVL (phiếu nhập/PO) − Tiền NVL lý thuyết (số món bán × công
 * thức × giá vốn). Không đối chiếu tồn kho đầu/cuối kỳ — xem ghi chú trong data_quality khi số
 * liệu có vẻ bất thường (thường do giá nhập/quy cách đóng gói sai, không phải hao hụt thật).
 *
 * #151411 (PA1 của #142448) — 2 sửa đổi chống thổi phồng số hao hụt:
 *  1. Vật tư không nằm trong công thức món nào (ống hút, túi rác…) đã được backend tách sang
 *     `supplies[]`, KHÔNG còn nằm trong `materials[]`, `summary` hay `qty_summary` — hiển thị
 *     riêng ở khối "Vật tư vận hành" chỉ với tổng tiền mua, không gắn nhãn hao hụt.
 *  2. Mặc định xem THEO THÁNG (trước đây là 'day'). NVL nhập theo lô làm số ở mức nhỏ hơn tháng
 *     dương giả ngay ngày nhập, nên chọn ngày/tuần sẽ hiện cảnh báo ngay trên màn.
 *
 * #153458 (PA2 + PA3 của #142448) — ĐỔI GỐC CÔNG THỨC:
 *  - Các cục chính nay lấy từ `balance_qty_summary`: hao hụt = tồn đầu kỳ + nhập trong kỳ − xuất
 *    theo định mức − chuyển kho ra − tồn cuối kỳ. Con số của công thức CŨ (mua − lý thuyết) KHÔNG
 *    bị xoá: vẫn đặt ngay cạnh để đọc được chênh lệch giữa hai cách tính.
 *  - Thêm khối "Đối chiếu số đếm tay theo ca" (PA3): số hệ thống vs số nhân viên đếm tay, kèm
 *    trạng thái rõ ràng cho NVL chưa có số đếm tay thay vì để trống.
 *
 * #172026 — RÚT GỌN MÀN MẶC ĐỊNH (lệnh sếp: "bảng hao hụt nvl hiện tại chưa hợp lý, bỏ những thứ
 * không cần quan sát"). Sau 3 đợt thêm liên tiếp ở trên, màn này chất đống tới mức không đọc ra
 * được điều duy nhất cần biết: NVL NÀO ĐANG MẤT BẤT THƯỜNG. Nay:
 *  - MẶC ĐỊNH chỉ còn 3 cục chính (công thức mới, theo số lượng) + bảng 4 cột
 *    (Tên NVL · % hao hụt · Số lượng lệch · Tiền hao hụt), NVL mất nhiều TIỀN nhất lên đầu
 *    (#192444 đổi cột cuối từ "Cờ cảnh báo" sang "Tiền hao hụt" và đổi luôn khoá sắp xếp).
 *  - MỌI thứ còn lại KHÔNG XOÁ, chỉ lui sau nút "Xem số liệu chi tiết": số theo tiền, số của công
 *    thức cũ + biểu đồ so sánh, khối tồn đầu/nhập/định mức/tồn cuối, khối đối chiếu số đếm tay,
 *    khối vật tư vận hành (đặt cuối cùng), cột SL mua vào / SL tiêu thụ.
 *  - Kỳ bắt đầu vào/trước ngày sổ cái kho được mở thì thay số bằng chữ "chưa đo được" thay vì hiện
 *    % âm rất sâu trông như số thật (mốc lấy từ `balance_measurable_from` của backend, không gõ
 *    cứng ngày vào giao diện).
 */
/**
 * #172026 bổ sung 3 — dòng KIỂM CA trong khối chi tiết, viết thành câu đọc được.
 * Bỏ hẳn chữ "trung vị": trung vị đang tính lẫn cả các lần khớp nên gần như luôn ra 0,0% và không
 * nói lên điều gì (xem bổ sung của #172543). Thay bằng lần lệch NHIỀU NHẤT — đó mới là thứ đáng
 * nhìn khi muốn biết số đếm tay có mâu thuẫn với hệ thống không.
 */
function shiftCountSentence(m) {
  const sc = m.shift_count;
  if (!sc || sc.status === 'NOT_FLAGGED') {
    return 'Chưa bật đếm tay cho nguyên vật liệu này.';
  }
  if (sc.status === 'FLAGGED_NO_COUNT') {
    return 'Đã bật đếm tay nhưng kỳ này chưa có số đếm nào dùng được để đối chiếu.';
  }
  // #172026 bổ sung 3 (kiểm thử ĐẠT #181731, AC7): viết thành 1 câu đọc được, bỏ hẳn "trung vị",
  // chỉ dùng lần lệch NHIỀU NHẤT (max_abs_variance_pct) — thay cho bản JSX "lệch điển hình" cũ
  // của #172543 (đã bị thay thế theo đúng đặc tả sếp chốt sau).
  const head = `Nhân viên đếm tay ${sc.checks_count} lần, lệch ${sc.checks_with_variance} lần`;
  return sc.max_abs_variance_pct != null
    ? `${head}, lệch nhiều nhất ${fmtPct(sc.max_abs_variance_pct)}.`
    : `${head}.`;
}



export default function InventoryShrinkageTab({ branchId, materials, currentUserId }) {
  const [range, setRange] = useState(() => calcDatePreset('thisMonth'));
  // #151411: mặc định THEO THÁNG — mức nhỏ hơn tháng bị lô hàng nhập trong kỳ làm dương giả.
  const [granularity, setGranularity] = useState('month');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const [flagged, setFlagged] = useState(() => new Set());
  const [togglingFlag, setTogglingFlag] = useState({});
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  // #172026 bổ sung 3: trong khối chi tiết của 1 dòng NVL, phần tiền + số liệu gốc gấp lại,
  // mặc định ĐÓNG. Không xoá số nào, chỉ không hiện ngay.
  const [expandedRawRows, setExpandedRawRows] = useState(() => new Set());
  // #142018 rồi #172026: mọi số liệu phụ (tiền, công thức cũ, biểu đồ, tồn đầu/cuối, đối chiếu
  // đếm tay, vật tư vận hành) KHÔNG bị xoá — chỉ lui hết về sau nút "Xem số liệu chi tiết".
  const [showDetails, setShowDetails] = useState(false);
  // #172026 bổ sung 1 (lệnh sếp 28/08/2026: "còn mấy cái này sao k ẩn đi khi nào cần xem thì bấm
  // mở ra") — khối "Dữ liệu chưa sẵn sàng" đang xổ hết mấy trang danh sách ra màn. Nay mặc định
  // chỉ còn 1 dòng tiêu đề kèm số lượng, ĐÓNG sẵn; nội dung bên trong giữ nguyên không đổi.
  const [showDataQuality, setShowDataQuality] = useState(false);

  const toggleExpand = useCallback((materialId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(materialId)) next.delete(materialId);
      else next.add(materialId);
      return next;
    });
  }, []);

  const toggleRaw = useCallback((materialId) => {
    setExpandedRawRows((prev) => {
      const next = new Set(prev);
      if (next.has(materialId)) next.delete(materialId);
      else next.add(materialId);
      return next;
    });
  }, []);

  const materialsById = useMemo(
    () => new Map((materials || []).map((m) => [m.id, m])),
    [materials],
  );

  const fetchFlags = useCallback(async () => {
    if (!branchId) {
      setFlagged(new Set());
      return;
    }
    try {
      const rows = await inventoryAPI.getShiftCheckFlags(branchId);
      setFlagged(new Set((rows || []).map((r) => r.material_id)));
    } catch (err) {
      toast.error(err?.message || 'Không thể tải danh sách NVL gắn cờ kiểm ca');
    }
  }, [branchId]);

  useEffect(() => { fetchFlags(); }, [fetchFlags]);

  const handleToggleFlag = useCallback(async (materialId, materialName) => {
    if (!branchId) return;
    setTogglingFlag((prev) => ({ ...prev, [materialId]: true }));
    try {
      const isRequired = !flagged.has(materialId);
      await inventoryAPI.setShiftCheckFlag(branchId, materialId, {
        is_required: isRequired,
        current_user_id: currentUserId,
      });
      toast.success(
        isRequired
          ? `Đã gắn cờ kiểm ca cho ${materialName}`
          : `Đã bỏ cờ kiểm ca cho ${materialName}`,
      );
      await fetchFlags();
    } catch (err) {
      toast.error(err?.message || 'Không thể cập nhật cờ kiểm ca');
    } finally {
      setTogglingFlag((prev) => {
        const next = { ...prev };
        delete next[materialId];
        return next;
      });
    }
  }, [branchId, currentUserId, flagged, fetchFlags]);

  const fetchReport = useCallback(async () => {
    if (!range.from || !range.to) return;
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);
      setError(null);
      const params = { from_date: range.from, to_date: range.to, granularity };
      if (branchId) params.branch_id = branchId;
      const data = await inventoryAPI.getShrinkageReport(params);
      if (requestIdRef.current !== requestId) return; // a newer request already superseded this one
      setReport(data);
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      const message = err?.message || 'Không thể tải báo cáo hao hụt NVL';
      toast.error(message);
      setError(message);
      setReport(null);
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  }, [branchId, range, granularity]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const chartData = useMemo(
    () => (report?.buckets || []).map((b) => ({
      label: b.key,
      purchase: b.purchase_value,
      theoretical: b.theoretical_value,
    })),
    [report],
  );

  // #142018: biểu đồ chính theo SỐ LƯỢNG — trung vị % của từng NVL ở mỗi mốc (không gộp số lượng
  // khác đơn vị), kèm số NVL vượt ngưỡng trong tooltip.
  // #153458: đường chính là công thức MỚI, đường đứt nét là công thức CŨ — đặt trên cùng một trục.
  // #172026: biểu đồ này lui về sau nút "Xem số liệu chi tiết".
  const qtyChartData = useMemo(
    () => (report?.buckets || []).map((b) => ({
      label: b.key,
      medianPct: b.balance_qty_median_pct,
      legacyMedianPct: b.qty_variance_pct_median,
      overThreshold: b.balance_qty_materials_over_threshold ?? 0,
    })),
    [report],
  );

  const dq = report?.data_quality ?? emptyDataQuality;
  // #172026: 3 nhóm cảnh báo của #153458 (bút toán 1000×, thiếu tồn đầu kỳ, ghi lùi ngày) trước
  // đây được VẼ nhưng không được ĐẾM — kỳ nào chỉ dính đúng 3 nhóm đó thì cả khối cảnh báo biến
  // mất vì `dqCount === 0`. Nay đếm đủ để khối luôn hiện khi thật sự có vấn đề dữ liệu.
  const dqCount = dq.menu_items_without_recipe.length
    + dq.materials_without_price.length
    + dq.btp_data_issues.length
    + dq.suspicious_purchase_pricing.length
    + dq.base_unit_package_purchases.length
    + (dq.orders_missing_deduction?.count ?? 0)
    + dq.unit_scale_stock_adjustments.length
    + dq.materials_without_opening_baseline.length
    + (dq.backdated_stock_transactions?.count ?? 0);

  const variance = report?.summary?.variance_value ?? 0;
  // variance_value = purchase_value − theoretical_value (backend): dương nghĩa là mua nhiều hơn
  // lý thuyết cần dùng → đó mới là hao hụt thật (đỏ). Âm chỉ là chưa dùng hết hàng mới mua.
  const varianceIsLoss = variance > 0;

  const qty = report?.qty_summary ?? null;
  // #153458: cục chính nay là công thức MỚI; `qty` (công thức cũ) lui về dòng đối chiếu bên dưới.
  const balanceQty = report?.balance_qty_summary ?? null;
  const balanceTop = balanceQty?.top_materials?.[0] ?? null;
  const shiftSummary = report?.shift_check_summary ?? null;
  // Bảng PA3 chỉ liệt kê NVL THỰC SỰ có chuyện để đối chiếu (có số đếm tay, hoặc có cờ mà kỳ này
  // chưa có số). NVL chưa bật kiểm ca không đổ vào bảng cho dài — trạng thái của chúng đã hiện
  // bằng chữ nhỏ "chưa đếm tay" ngay dưới tên NVL ở bảng chi tiết (#192444, trước là cột "Cờ
  // cảnh báo"), nên không có chỗ nào bị để trống.
  const shiftRows = (report?.materials ?? []).filter(
    (m) => m.shift_count && m.shift_count.status !== 'NOT_FLAGGED',
  );

  // #151411: vật tư không nằm trong công thức món nào — chỉ tổng tiền mua, KHÔNG phải hao hụt.
  const supplies = report?.supplies ?? [];
  const suppliesSummary = report?.supplies_summary ?? null;
  const granularityWarning = granularity !== 'month';

  // #172026: kỳ nằm trước lúc sổ cái kho được mở thì công thức tồn đầu/cuối kỳ KHÔNG đo được (mọi
  // NVL có tồn đầu kỳ = 0 suy ra ⇒ % âm rất sâu, sai nhưng trông như thật). Mốc do backend tính
  // từ giao dịch kho sớm nhất (`balance_measurable_from`) — không gõ cứng ngày vào giao diện.
  const measurableFrom = report?.balance_measurable_from ?? null;
  const ledgerFrom = report?.stock_ledger_available_from ?? null;
  const periodNotMeasurable = !!(measurableFrom && report?.from_date && report.from_date < measurableFrom);
  const notMeasurableText = `chưa đo được, hệ thống chưa có sổ tồn kho trước ${fmtDate(ledgerFrom)}`;

  /**
   * #192444 bổ sung 1 + 2 (lệnh sếp 731599156 / 731599157) — TIỀN HAO HỤT của từng NVL.
   *
   * Nguồn DUY NHẤT là `m.balance_shrinkage_value` — CHÍNH con số mà khối chi tiết in ra ở dòng
   * "⇒ THIẾU … ≈ …đ" (xem `ShrinkageEquation`). Cố ý KHÔNG tính lại bằng đường khác (qty × đơn
   * giá vốn ở FE): backend làm tròn tiền ở `inventory-shrinkage.service.ts:617`, tự nhân lại ở
   * đây sẽ lệch vài đồng so với dòng chi tiết — sếp chốt lệch 1 đồng cũng là sai.
   *
   * Dòng có mốc tồn kho không đáng tin, hoặc NVL chưa có giá vốn ở bất kỳ nguồn nào, thì KHÔNG
   * quy ra tiền: hiện "—" chứ không hiện "0đ" (0đ đọc thành "không mất đồng nào", sai hẳn nghĩa).
   */
  const moneyOf = useCallback((m) => (
    m.balance_baseline_unreliable || m.balance_shrinkage_value == null
      ? null
      : Number(m.balance_shrinkage_value)
  ), []);

  /**
   * #192444 bổ sung 3 (lệnh sếp 731599158 + 731599159: "tính bằng tiền") — MẪU SỐ của cột "%
   * trong tổng hao hụt".
   *
   * Sếp yêu cầu mẫu số đúng bằng ô "Hao hụt" ở đầu màn. Trên dữ liệu thật KHÔNG dùng được con số
   * đó, vì hai lý do đo được (số đo CN Quận Thủ Đức, 29/5–28/8/2026 — ghi trong báo cáo #192444):
   *   1. Ô đầu màn là công thức CŨ (tiền mua − tiền lý thuyết = −13.341.736,79đ), còn cột tiền
   *      này là công thức MỚI (tồn đầu + nhập − định mức − chuyển kho − tồn cuối). Hai công thức
   *      khác nhau nên tổng không thể khớp; chênh 8.139.493,50đ.
   *   2. Tổng RÒNG của công thức mới đang ÂM (−5.202.243,29đ) vì 6 NVL DƯ (−18.357.225,13đ) lớn
   *      hơn 21 NVL hao hụt (+13.154.981,84đ). Chia cho một mẫu số âm thì món hao hụt nhiều nhất
   *      ra % ÂM — vô nghĩa.
   * Nên mẫu số là TỔNG TIỀN HAO HỤT CỦA CÁC NVL ĐANG HAO HỤT (chỉ cộng phần dương). Nhờ vậy cột %
   * cộng lại ra ĐÚNG 100% như sếp yêu cầu tự kiểm; món DƯ hiện "—" (đúng tinh thần #172543: dư
   * không tô đỏ, và "% của một khoản dư trong tổng thất thoát" không có nghĩa).
   */
  const totalLossMoney = useMemo(() => (report?.materials ?? []).reduce((sum, m) => {
    const v = moneyOf(m);
    return v != null && v > 0 ? sum + v : sum;
  }, 0), [report, moneyOf]);

  /**
   * #205325 (sếp bấm nút Option 2 "Giữ nguyên ô đầu màn, chỉ ghi chú rõ 2 công thức khác nhau",
   * phiếu td20331924888 — sếp bấm nhầm nút 1 rồi tự đính chính sau 18 giây) — GIẢI THÍCH vì sao ô
   * "Hao hụt" đầu màn KHÔNG bằng tổng cột "Tiền hao hụt".
   *
   * ⚠️ Ô đầu màn CỐ Ý giữ nguyên công thức CŨ (`summary.variance_value` = tiền mua − tiền lý
   * thuyết). Sếp đã chốt KHÔNG đổi công thức ô đó — đừng "sửa cho khớp cột", đó là hướng đã bị
   * đính chính và đã revert (commit b4e0cfb → 20fc1c7). Việc của đợt này chỉ là NÓI RÕ BẰNG CHỮ
   * rằng hai con số là hai phép tính khác nhau.
   *
   * Ba con số dưới đây tính SỐNG từ chính dữ liệu đang hiện, KHÔNG gõ cứng: đề bài có nêu số mẫu
   * của CN Thủ Đức 29/5–28/8, nhưng gõ cứng thì sang chi nhánh/kỳ khác sẽ thành số sai.
   */
  // Tổng RÒNG theo công thức MỚI (đã trừ hàng dư) — con số backend tính, để đối chiếu.
  const balanceNetValue = report?.balance_summary?.shrinkage_value ?? null;
  // Chênh lệch giữa ô đầu màn và tổng cột đang hiện trên bảng.
  const gapHeadlineVsColumn = report?.summary?.variance_value != null
    ? Math.abs(report.summary.variance_value - totalLossMoney)
    : null;
  // Tiền hàng DƯ (dùng ít hơn định mức) — phần cột "Tiền hao hụt" cố ý KHÔNG trừ vào tổng.
  const surplusValue = balanceNetValue != null ? totalLossMoney - balanceNetValue : null;

  // #192444 bổ sung 1: mặc định xếp GIẢM DẦN THEO TIỀN thay vì theo %. Bấm tiêu đề đổi chiều.
  const [moneySortDir, setMoneySortDir] = useState('desc');

  // #192444 bổ sung 1: NVL mất NHIỀU TIỀN NHẤT lên đầu (trước đây là % cao nhất). Lý do sếp nêu ở
  // ảnh 19:37: % cao chưa chắc mất nhiều tiền — trên dữ liệu thật "Rich cao tiện dụng" +80,9% chỉ
  // 760.969đ trong khi "Sữa đặc" +43,0% mất tới 1.708.411đ. Dòng chưa quy ra tiền được (hiện "—")
  // luôn xuống cuối ở cả hai chiều — xếp chúng lên đầu theo một con số không có thì vô nghĩa.
  const sortedMaterials = useMemo(() => {
    const rows = [...(report?.materials ?? [])];
    const dir = moneySortDir === 'asc' ? -1 : 1;
    return rows.sort((a, b) => {
      const ka = moneyOf(a);
      const kb = moneyOf(b);
      if (ka == null && kb == null) return a.name.localeCompare(b.name, 'vi');
      if (ka == null) return 1;
      if (kb == null) return -1;
      return (kb - ka) * dir;
    });
  }, [report, moneySortDir, moneyOf]);

  return (
    <div>
      <Row className="mb-3 g-3 align-items-end">
        <Col md={8}>
          <DateRangePicker
            fromDate={range.from}
            toDate={range.to}
            onChange={(from, to) => setRange({ from, to })}
            defaultPreset="thisMonth"
          />
        </Col>
      </Row>

      {loading && <div className="text-center py-5" role="status" aria-label="Đang tải"><Spinner /></div>}

      {!loading && error && (
        <Alert variant="danger" className="d-flex align-items-center justify-content-between">
          <span>{error}</span>
          <Button size="sm" variant="outline-danger" onClick={fetchReport}>Thử lại</Button>
        </Alert>
      )}

      {!loading && !error && !report && (
        <p className="text-muted text-center py-4">Chọn khoảng thời gian để xem báo cáo.</p>
      )}

      {!loading && !error && report && (
        <>
          {report.data_available_from && report.from_date < report.data_available_from && (
            <Alert variant="secondary" className="small py-2">
              Hệ thống chỉ có dữ liệu đơn hàng từ <strong>{fmtDate(report.data_available_from)}</strong> —
              các ngày trước đó trong khoảng đã chọn sẽ không có số liệu.
            </Alert>
          )}

          {/* #172026: kỳ trước khi có sổ tồn kho — nói thẳng là chưa đo được, KHÔNG hiện con số. */}
          {periodNotMeasurable && (
            <Alert variant="warning" className="d-flex align-items-start gap-2 py-2">
              <AlertTriangle size={18} className="flex-shrink-0 mt-1" />
              <span>
                Khoảng thời gian đang chọn bắt đầu từ <strong>{fmtDate(report.from_date)}</strong> —{' '}
                <strong>{notMeasurableText}</strong>. Hao hụt cần một mốc tồn ĐẦU KỲ đo được; trước
                mốc đó mọi NVL bị coi như tồn đầu kỳ bằng 0 nên con số ra âm rất sâu và hoàn toàn
                sai. Hãy chọn khoảng bắt đầu từ <strong>{fmtDate(measurableFrom)}</strong> trở đi.
              </span>
            </Alert>
          )}

          {/* #172026 bổ sung 2 (lệnh sếp 28/08/2026: "còn cái này đưa lên đầu đi") — 3 cục tổng
              theo TIỀN lên ĐẦU TRANG, hiện mặc định, KHÔNG gấp lại. Đây là điều chỉnh NGƯỢC với đề
              bài gốc của chính việc này (gốc bảo dồn hết số theo tiền vào phần chi tiết); sếp ra
              lệnh trực tiếp nên làm theo sếp.

              #192444 (lệnh sếp 28/08/2026: "lấy TOÀN BỘ bản này đưa lên đầu, tôi đã nói rồi sao
              lại làm sai") — lần trước làm THIẾU: chỉ 3 cục tiền lên đầu, còn biểu đồ "Tiền mua vs
              tiền lý thuyết theo thời gian" vẫn nằm sâu trong "Xem số liệu chi tiết" nên khối tiền
              bị tách đôi. Nay cả CỤM 4 thành phần (3 cục + biểu đồ tiền) nằm liền nhau ở đầu màn,
              đúng thứ tự trong ảnh sếp gửi. Đừng tách biểu đồ này xuống lại. */}
          <Row className="g-3 mb-3">
            <Col md={4}>
              <Card className="text-center h-100">
                <Card.Body>
                  <div className="h4 fw-bold text-primary mb-0">{fmtCurrency(report.summary.purchase_value)}</div>
                  <div className="small text-muted mt-1">Tiền mua NVL (phiếu nhập/đặt hàng)</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center h-100">
                <Card.Body>
                  <div className="h4 fw-bold mb-0" style={{ color: '#e8590c' }}>{fmtCurrency(report.summary.theoretical_value)}</div>
                  <div className="small text-muted mt-1">Tiền NVL lý thuyết (theo công thức × số bán)</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className={`text-center h-100 ${varianceIsLoss ? 'border-danger' : 'border-success'}`}>
                <Card.Body>
                  <div className={`h4 fw-bold mb-0 ${varianceIsLoss ? 'text-danger' : 'text-success'}`}>
                    {fmtCurrency(report.summary.variance_value)}
                  </div>
                  <div className="small text-muted mt-1">
                    Hao hụt ({fmtPct(report.summary.variance_pct)})
                  </div>
                  {/* #205325 (Option 2): ô này GIỮ NGUYÊN công thức cũ — chỉ nói rõ nó là phép
                      tính KHÁC với cột "Tiền hao hụt" ở bảng dưới, để không ai cộng cột rồi
                      tưởng máy tính sai. Chữ nhỏ, nhạt, không thêm hàng nào vào bố cục. */}
                  <div className="small text-muted lh-sm mt-1">
                    Công thức cũ: tiền mua − tiền lý thuyết. <strong>Khác</strong> công thức của
                    cột <strong>Tiền hao hụt</strong> ở bảng dưới nên hai số không bằng nhau —
                    giải thích ở cuối bảng.
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* #192444: thành phần thứ 4 của khối tiền — biểu đồ đi liền 3 cục trên, không tách. */}
          <Card className="mb-3">
            <Card.Body>
              <Card.Title className="h6 mb-2">Tiền mua vs tiền lý thuyết theo thời gian</Card.Title>
              <ShrinkageChartComponent data={chartData} />
            </Card.Body>
          </Card>

          {/* Hàng cục theo SỐ LƯỢNG, ngay dưới khối tiền. #172026 bổ sung 2 đã bỏ cục TRUNG VỊ %
              (gộp NVL khác đơn vị nên vô nghĩa — trung vị lui xuống "Xem số liệu chi tiết").
              #192444 (ảnh sếp gạch chéo đỏ 2 ô, 28/08/2026): ĐÃ BỎ KHỎI GIAO DIỆN 2 cục
              "NVL hao hụt vượt ngưỡng X% số lượng" và "NVL hao nhiều nhất: <tên>". Chỉ bỏ ở tầng
              HIỂN THỊ — `balanceQty` / `balanceTop` vẫn được tính và vẫn dùng ở dòng tóm tắt trong
              card "Nguyên vật liệu nào đang hao hụt" cùng bảng chi tiết, field API không đụng tới.
              Đừng thêm 2 cục này lại. */}
          <Row className="g-3 mb-2">
            <Col md={4}>
              <Card className="text-center h-100">
                <Card.Body>
                  <div className="h5 fw-bold mb-0">
                    {fmtDate(report.from_date)} – {fmtDate(report.to_date)}
                  </div>
                  <div className="small text-muted mt-1">Kỳ đang xem</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* #172026: đúng MỘT dòng chú thích ở màn mặc định — cách tính, số NVL bị loại vì mốc tồn
              bẩn, và một con trỏ tới khối cảnh báo dữ liệu (khối đó nay nằm trong phần chi tiết). */}
          <p className="small text-muted mb-3">
            Cách tính: tồn đầu kỳ + nhập trong kỳ − lượng theo định mức (công thức × món đã bán) −
            chuyển kho ra − tồn cuối kỳ. Hàng mua về mà chưa bán không bị tính thành hao hụt.
            {!periodNotMeasurable && balanceQty?.materials_excluded_unreliable > 0 && (
              <>
                {/* #192444: 2 cục số lượng ở trên đã bị bỏ, nên câu này không còn "các con số
                    trên" để trỏ vào — nói thẳng là loại khỏi các con số HAO HỤT SỐ LƯỢNG. */}
                {' '}Đã loại <strong>{balanceQty.materials_excluded_unreliable}</strong> NVL khỏi các
                con số hao hụt theo số lượng vì mốc tồn kho không đáng tin.
              </>
            )}
            {dqCount > 0 && (
              <>
                {' '}Có <strong>{dqCount}</strong> vấn đề dữ liệu khiến số hao hụt có thể chưa
                chính xác — bấm <strong>Xem số liệu chi tiết</strong> để mở ra.
              </>
            )}
          </p>

          <Card>
            <Card.Body>
              <Card.Title className="h6">Nguyên vật liệu nào đang hao hụt</Card.Title>
              {/* #172026 bổ sung 2 — dòng tóm tắt THAY CHỖ bảng gộp theo đơn vị vừa gỡ. Nói bằng
                  chữ đúng hai điều dùng để ra quyết định, không cộng chéo đơn vị. */}
              {!periodNotMeasurable && balanceQty && (
                <p className="mb-2">
                  <strong>{balanceQty.materials_over_threshold}</strong> trong tổng số{' '}
                  <strong>{balanceQty.materials_measurable}</strong> nguyên vật liệu đang hao hụt vượt
                  ngưỡng {balanceQty.threshold_pct}%
                  {balanceTop && (
                    <>
                      {' '}· hao nhiều nhất là <strong>{balanceTop.name}</strong>{' '}
                      <strong className="text-danger">{fmtPct(balanceTop.shrinkage_pct_available)}</strong>
                    </>
                  )}
                  .
                </p>
              )}
              {report.materials.length === 0 ? (
                <p className="text-muted text-center py-3 mb-0">Không có tiêu thụ NVL trong khoảng thời gian này.</p>
              ) : (
                <>
                  {/* #172026: đúng 4 cột. Mọi số khác (tiền, công thức cũ, tồn đầu/cuối, SL mua vào,
                      SL tiêu thụ, nguồn giá, ô Kiểm ca) vẫn còn nguyên — nằm trong phần mở rộng của
                      từng dòng, bấm mũi tên đầu dòng để xem.
                      #192444 bổ sung 1/2/3: cột cuối "Cờ cảnh báo" ĐỔI thành "Tiền hao hụt" (sếp:
                      "thay cột này bằng số liệu gì đó cần thiết và ý nghĩa nhất đi"). Cột cũ chỉ
                      lặp đúng 2 câu chữ, chiếm cả một cột mà không giúp quyết định gì. Chữ cảnh báo
                      KHÔNG bị xoá — chuyển thành dòng nhỏ xám ngay dưới TÊN NVL, nên bảng vẫn đúng
                      5 cột, không rộng thêm (sếp xem bằng điện thoại). */}
                  {/* #192444 bổ sung 3 — `small`: đo thật trên khung điện thoại 390px, bảng cần
                      341px trong khi chỗ trống chỉ có 324px nên cột tiền bị cắt mất đuôi. Hạ cỡ
                      chữ toàn bảng là cách duy nhất giữ đủ 5 cột mà không phải cắt bớt con số —
                      cắt số thì cột tiền hết khớp từng đồng với dòng "⇒ THIẾU", là điều sếp cấm. */}
                  <Table
                    bordered
                    size="sm"
                    responsive
                    hover
                    className="small"
                    // `table-layout: fixed` + bề rộng theo % — cách DUY NHẤT chặn được việc bảng tự
                    // nới rộng quá khung rồi cắt mất cột cuối. Với layout tự động, tổng bề rộng
                    // cần thiết đo được là 327px trong khi chỗ trống chỉ 324px (CN Quận 10), và
                    // hạ cỡ chữ KHÔNG chữa được (đo thật: hạ xuống 0,8rem lại thành 352px). Cố
                    // định tỷ lệ thì mọi chi nhánh, mọi bộ dữ liệu đều vừa khít.
                    style={{ '--bs-table-cell-padding-x': '0.2rem', tableLayout: 'fixed' }}
                  >
                    <thead>
                      <tr>
                        {/* #192444: bề rộng cố định theo % — cột "Tiền hao hụt" được phần rộng
                            nhất vì con số tiền không được phép cắt bớt hay xuống dòng. */}
                        <th style={{ width: '7%' }} />
                        <th style={{ width: '25%' }}>Tên NVL</th>
                        <th className="text-end" style={{ width: '17%' }}>% hao hụt</th>
                        <th className="text-end" style={{ width: '20%' }}>Số lượng lệch</th>
                        <th className="text-end" style={{ width: '31%' }}>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none text-reset fw-bold"
                            // `.btn` của Bootstrap không cho chữ xuống dòng, khiến tiêu đề dài
                            // đẩy cả bảng rộng quá khung điện thoại (đo thật: 327px > 324px ở CN
                            // Quận 10). Cho phép xuống dòng thì bảng vừa khít, không cắt cột nào.
                            style={{ whiteSpace: 'normal' }}
                            onClick={() => setMoneySortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
                            title="Bấm để đổi chiều sắp xếp theo tiền hao hụt"
                            aria-label={`Tiền hao hụt — đang xếp ${moneySortDir === 'desc' ? 'giảm dần' : 'tăng dần'}, bấm để đổi chiều`}
                          >
                            Tiền hao hụt {moneySortDir === 'desc' ? '↓' : '↑'}
                          </Button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedMaterials.map((m) => {
                        const mat = materialsById.get(m.material_id) ?? { unit: m.unit };
                        // Dương = mua nhiều hơn lý thuyết cần dùng → hao hụt thật (đỏ).
                        const isLoss = (m.variance_value ?? 0) > 0;
                        // #142018: tô cảnh báo theo SỐ LƯỢNG (dương = mua nhiều hơn công thức cần
                        // → hao hụt), độc lập với giá vốn.
                        const isQtyLoss = (m.variance_qty ?? 0) > 0;
                        // #153458: dương = mất hàng thật theo công thức mới. Dòng có mốc tồn bẩn thì
                        // KHÔNG hiện số (hiện '—') thay vì hiện một con số sai trông như thật.
                        const isBalanceUnusable =
                          m.balance_baseline_unreliable || m.balance_shrinkage_pct_available == null;
                        const isBalanceLoss = (m.balance_shrinkage_qty ?? 0) > 0;
                        const isExpanded = expandedRows.has(m.material_id);
                        const warnings = materialWarnings(m);
                        // #192444: tiền hao hụt + phần trăm trong tổng tiền hao hụt của kỳ.
                        const money = moneyOf(m);
                        const lossShare = money != null && money > 0 && totalLossMoney > 0
                          ? (money / totalLossMoney) * 100
                          : null;
                        return (
                          <React.Fragment key={m.material_id}>
                            <tr>
                              <td className="text-center align-middle">
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0"
                                  onClick={() => toggleExpand(m.material_id)}
                                  aria-label={isExpanded ? 'Thu gọn số liệu chi tiết' : 'Xem số liệu chi tiết'}
                                >
                                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                </Button>
                              </td>
                              {/* #192444 bổ sung 1: chữ cảnh báo của cột "Cờ cảnh báo" vừa gỡ nay
                                  nằm đây — chữ nhỏ xám ngay dưới tên NVL. Không thêm cột, không
                                  làm bảng rộng thêm. NVL "Không có cảnh báo" thì không hiện gì
                                  (đúng yêu cầu sếp), nên bảng gọn hẳn so với trước. */}
                              <td className="align-middle">
                                <div>{m.name}</div>
                                {warnings.map((w) => (
                                  <div key={w} className="small text-muted lh-sm" title={w}>
                                    {WARNING_SHORT[w] ?? w}
                                  </div>
                                ))}
                              </td>
                              <td className={`text-end fw-medium align-middle ${periodNotMeasurable || isBalanceUnusable ? 'text-muted' : isBalanceLoss ? 'text-danger' : 'text-success'}`}>
                                {periodNotMeasurable
                                  ? 'chưa đo được'
                                  : isBalanceUnusable ? '—' : fmtPct(m.balance_shrinkage_pct_available)}
                              </td>
                              <td className={`text-end align-middle ${periodNotMeasurable || isBalanceUnusable ? 'text-muted' : isBalanceLoss ? 'text-danger fw-medium' : 'text-success fw-medium'}`}>
                                {periodNotMeasurable
                                  ? 'chưa đo được'
                                  : isBalanceUnusable
                                    ? '—'
                                    : <QtyDual quantityInBase={m.balance_shrinkage_qty} material={mat} />}
                              </td>
                              {/* #192444 bổ sung 1+2+3 — CỘT CUỐI: tiền hao hụt của chính NVL này,
                                  lấy nguyên `balance_shrinkage_value` nên khớp TỪNG ĐỒNG với dòng
                                  "⇒ THIẾU … ≈ …đ" trong khối chi tiết ngay dưới. Dòng nhỏ xám bên
                                  dưới là % của khoản đó trong tổng tiền hao hụt — cố ý ghép chung
                                  MỘT ô thay vì mở thêm cột thứ 6, vì sếp xem bằng điện thoại và
                                  bảng đã bị cắt chữ ở 4 cột. */}
                              <td className="text-end align-middle">
                                {periodNotMeasurable || money == null ? (
                                  <span className="text-muted">—</span>
                                ) : money > 0 ? (
                                  <>
                                    <div className="text-danger fw-medium text-nowrap">{fmtCurrency(money)}</div>
                                    {lossShare != null && (
                                      <div className="small text-muted lh-sm">{fmtShare(lossShare)}</div>
                                    )}
                                  </>
                                ) : (
                                  // Dư = dùng ÍT hơn định mức. KHÔNG tô đỏ (chốt ở #172543), và
                                  // KHÔNG có % vì "phần trăm của một khoản dư trong tổng thất
                                  // thoát" không có nghĩa — hiện "—" cho đúng.
                                  <>
                                    <div className="text-success fw-medium text-nowrap">
                                      dư {fmtCurrency(Math.abs(money))}
                                    </div>
                                    <div className="small text-muted lh-sm">—</div>
                                  </>
                                )}
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={5} className="bg-light">
                                  {/*
                                    #172543 — DÒNG TỰ GIẢI THÍCH, đặt TRÊN mọi con số khác.
                                    Sếp bắt lỗi 28/08/2026 ("bảng số liệu linh tinh này chưa giải
                                    thích được mấy số hao hụt ở trên"): khối dưới cho đủ 4 vế nhưng
                                    không nói chúng ghép lại thế nào, mỗi ô lại hiện 2 đơn vị (quy
                                    cách đóng gói ở trên, đơn vị cơ sở ở dưới) nên người đọc phải tự
                                    đoán vế nào cộng với vế nào, và rất dễ đọc nhầm dấu phân cách
                                    kiểu Việt Nam ("2.052,725" = 2.052,725 chứ không phải hai triệu).
                                    Dòng này viết thẳng phép tính bằng MỘT đơn vị duy nhất để cộng
                                    tay lại được.
                                  */}
                                  <ShrinkageEquation
                                    m={m}
                                    mat={mat}
                                    blockedText={
                                      periodNotMeasurable
                                        ? `Chưa tính được — ${notMeasurableText}.`
                                        : m.balance_baseline_unreliable
                                          ? 'Chưa tính được — tồn kho đầu kỳ của nguyên vật liệu này đang sai do lỗi nhập đơn vị, cần kiểm lại sổ kho.'
                                          : m.opening_baseline_missing
                                            ? 'Chưa tính được — kỳ này chưa đo được tồn đầu kỳ của nguyên vật liệu này.'
                                            : isBalanceUnusable
                                              ? 'Chưa tính được — mốc tồn kho của nguyên vật liệu này trong kỳ đang xem chưa dùng được.'
                                              : null
                                    }
                                  />

                                  {/* #172026 bổ sung 3: dòng kiểm ca giữ lại nhưng viết thành câu. */}
                                  <div className="small mb-2 d-flex align-items-center gap-3 flex-wrap">
                                    <span className={m.shift_count?.checks_with_variance > 0 ? 'text-danger' : 'text-muted'}>
                                      {shiftCountSentence(m)}
                                    </span>
                                    {branchId && mat.type !== 'SEMI_FINISHED' && (
                                      <span className="d-flex align-items-center gap-2 text-muted">
                                        Bật đếm tay mỗi ca
                                        <Form.Check
                                          type="switch"
                                          id={`shift-check-${m.material_id}`}
                                          checked={flagged.has(m.material_id)}
                                          onChange={() => handleToggleFlag(m.material_id, m.name)}
                                          disabled={!!togglingFlag[m.material_id]}
                                        />
                                        {togglingFlag[m.material_id] && <span className="spinner-border spinner-border-sm" />}
                                      </span>
                                    )}
                                  </div>

                                  {/* #172026 bổ sung 3: TOÀN BỘ số theo tiền + số liệu gốc (đơn vị
                                      G/ML) + số của công thức cũ đẩy xuống đây và GẤP LẠI. Không xoá
                                      số nào — chỉ để phép tính bên trên đọc được trước. */}
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 text-decoration-none small"
                                    onClick={() => toggleRaw(m.material_id)}
                                  >
                                    {expandedRawRows.has(m.material_id)
                                      ? <ChevronDown size={14} className="me-1" />
                                      : <ChevronRight size={14} className="me-1" />}
                                    Số liệu tiền và số liệu gốc
                                  </Button>

                                  {expandedRawRows.has(m.material_id) && (
                                  <Row className="g-3 small py-2">
                                    <Col xs={6} md={3}>
                                      <div className="text-muted">Tiền mua</div>
                                      <div>{m.purchase_value > 0 ? fmtCurrency(m.purchase_value) : '—'}</div>
                                    </Col>
                                    <Col xs={6} md={3}>
                                      <div className="text-muted">Tiền lý thuyết</div>
                                      <div>{m.value != null ? fmtCurrency(m.value) : '—'}</div>
                                    </Col>
                                    <Col xs={6} md={3}>
                                      <div className="text-muted">Hao hụt (tiền, công thức mới)</div>
                                      <div className={isBalanceUnusable ? 'text-muted' : isBalanceLoss ? 'text-danger fw-medium' : 'text-success fw-medium'}>
                                        {isBalanceUnusable ? '—' : fmtCurrency(m.balance_shrinkage_value)}
                                      </div>
                                    </Col>
                                    <Col xs={6} md={3}>
                                      <div className="text-muted">Hao hụt (tiền, công thức cũ)</div>
                                      <div className={m.variance_value == null ? '' : isLoss ? 'text-danger fw-medium' : 'text-success fw-medium'}>
                                        {m.variance_value != null ? fmtCurrency(m.variance_value) : '—'}
                                      </div>
                                    </Col>
                                    <Col xs={6} md={3}>
                                      <div className="text-muted">Đơn giá vốn</div>
                                      <div>{m.unit_cost != null ? fmtCurrency(m.unit_cost) : '—'}</div>
                                    </Col>
                                    <Col xs={6} md={3}>
                                      <div className="text-muted">Nguồn giá</div>
                                      <div>
                                        {m.cost_source === 'PO_PERIOD' && <Badge bg="success">Đơn hàng trong kỳ</Badge>}
                                        {m.cost_source === 'PO_LATEST' && <Badge bg="info">Đơn hàng gần nhất</Badge>}
                                        {m.cost_source === 'SUPPLIER_DEFAULT' && <Badge bg="secondary">Giá mặc định NCC</Badge>}
                                        {m.cost_source === null && <Badge bg="warning" text="dark">Chưa có giá</Badge>}
                                      </div>
                                    </Col>
                                    <Col xs={6} md={3}>
                                      <div className="text-muted">% hao hụt tiền so lý thuyết</div>
                                      <div className={m.variance_pct_theoretical == null ? '' : isLoss ? 'text-danger fw-medium' : 'text-success fw-medium'}>
                                        {fmtPct(m.variance_pct_theoretical)}
                                      </div>
                                    </Col>
                                    <Col xs={6} md={3}>
                                      <div className="text-muted">% hao hụt tiền so đã mua</div>
                                      <div className={m.variance_pct_purchase == null ? '' : isLoss ? 'text-danger fw-medium' : 'text-success fw-medium'}>
                                        {fmtPct(m.variance_pct_purchase)}
                                      </div>
                                    </Col>
                                    <Col xs={6} md={3}>
                                      <div className="text-muted">SL mua vào</div>
                                      <div>
                                        {m.purchase_qty > 0
                                          ? <QtyDual quantityInBase={m.purchase_qty} material={mat} />
                                          : '—'}
                                      </div>
                                    </Col>
                                    <Col xs={6} md={3}>
                                      <div className="text-muted">SL tiêu thụ</div>
                                      <div><QtyDual quantityInBase={m.qty_consumed} material={mat} /></div>
                                    </Col>
                                    <Col xs={6} md={3}>
                                      <div className="text-muted">% hao hụt SL (công thức cũ)</div>
                                      <div className={m.variance_pct_qty_theoretical == null ? '' : isQtyLoss ? 'text-danger fw-medium' : 'text-success fw-medium'}>
                                        {fmtPct(m.variance_pct_qty_theoretical)}
                                      </div>
                                    </Col>
                                    <Col xs={6} md={3}>
                                      <div className="text-muted">Chênh lệch SL (công thức cũ)</div>
                                      <div className={isQtyLoss ? 'text-danger fw-medium' : 'text-success fw-medium'}>
                                        {m.variance_qty != null
                                          ? `${Number(m.variance_qty).toLocaleString('vi-VN')} ${m.unit ?? ''}`
                                          : '—'}
                                      </div>
                                    </Col>
                                    <Col xs={12}>
                                      <div className="text-muted">
                                        Số quy đổi ra đơn vị gốc ({m.unit ?? ''}) — dùng để đối chiếu với sổ kho
                                      </div>
                                      <div className="font-monospace">
                                        {fmtQtyBase(m.opening_qty)} (tồn đầu) + {fmtQtyBase(m.inflow_qty)} (nhập) −{' '}
                                        {fmtQtyBase(m.theoretical_qty)} (định mức)
                                        {m.transfer_out_qty > 0 && ` − ${fmtQtyBase(m.transfer_out_qty)} (chuyển kho ra)`}
                                        {' '}− {fmtQtyBase(m.closing_qty)} (tồn cuối) ={' '}
                                        <span className={isBalanceUnusable ? 'text-muted' : isBalanceLoss ? 'text-danger' : 'text-success'}>
                                          {isBalanceUnusable ? '—' : `${fmtQtyBase(m.balance_shrinkage_qty)} ${m.unit ?? ''}`}
                                        </span>
                                      </div>
                                    </Col>
                                  </Row>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                      {/* #192444 bổ sung 3 — DÒNG TỔNG. Đây chính là MẪU SỐ của cột %, in ra để
                          cộng tay kiểm lại được; % của các dòng hao hụt cộng lại đúng 100,0%. */}
                      {!periodNotMeasurable && totalLossMoney > 0 && (
                        <tr className="table-light fw-medium">
                          <td />
                          {/* #205325 (Option 2): nói ngay tại dòng TỔNG rằng con số này KHÔNG
                              phải ô "Hao hụt" đầu màn — đây đúng chỗ người xem hay so nhầm. */}
                          <td colSpan={3}>
                            Tổng tiền hao hụt (chỉ các NVL đang hao hụt)
                            <div className="small text-muted lh-sm fw-normal">
                              không phải ô "Hao hụt" đầu màn — khác công thức
                            </div>
                          </td>
                          <td className="text-end">
                            <div className="text-danger text-nowrap">{fmtCurrency(totalLossMoney)}</div>
                            <div className="small text-muted lh-sm">100,0%</div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>

                  {/* #205325 (lệnh sếp qua nút Option 2 "Giữ nguyên ô đầu màn, chỉ ghi chú rõ 2
                      công thức khác nhau", phiếu td20331924888) — KHỐI GIẢI THÍCH vì sao ô "Hao
                      hụt" đầu màn khác tổng cột này. Đặt NGAY DƯỚI dòng TỔNG vì đó là lúc người
                      xem vừa cộng xong cột và đang thắc mắc. Mọi con số tính SỐNG từ dữ liệu đang
                      hiện, không gõ cứng theo chi nhánh/kỳ nào. */}
                  {!periodNotMeasurable && totalLossMoney > 0 && gapHeadlineVsColumn != null && (
                    <div className="border rounded bg-light p-2 mb-2 small lh-sm">
                      <div className="fw-medium mb-1">
                        Vì sao ô "Hao hụt" ở đầu màn ({fmtCurrency(report.summary.variance_value)})
                        không bằng dòng Tổng này ({fmtCurrency(totalLossMoney)})?
                      </div>
                      <div className="text-muted">
                        Hai con số là <strong>hai phép tính khác nhau</strong>, không phải một cái
                        đúng một cái sai — chênh nhau{' '}
                        <strong>{fmtCurrency(gapHeadlineVsColumn)}</strong>.
                      </div>
                      <ul className="mb-1 mt-1 ps-3 text-muted">
                        <li>
                          <strong>Ô đầu màn</strong> = tiền mua NVL − tiền NVL lý thuyết. Phụ thuộc
                          nhịp <em>mua hàng</em>: mua nhiều mà chưa dùng cũng làm số này phồng lên.
                        </li>
                        <li>
                          <strong>Cột "Tiền hao hụt"</strong> = tồn đầu kỳ + nhập trong kỳ − lượng
                          theo định mức − chuyển kho ra − tồn cuối kỳ. Bám <em>tồn kho thật</em>,
                          không bị nhịp mua hàng làm nhiễu.
                        </li>
                        {surplusValue != null && surplusValue > 0 && (
                          <li>
                            Dòng Tổng chỉ cộng <strong>phần hao hụt</strong>. Kỳ này còn{' '}
                            <strong>{fmtCurrency(surplusValue)}</strong> tiền hàng <strong>dư</strong>{' '}
                            (dùng ít hơn định mức) chưa trừ vào — trừ vào thì tổng ròng theo công
                            thức mới là <strong>{fmtCurrency(balanceNetValue)}</strong>.
                          </li>
                        )}
                      </ul>
                      <div className="text-muted">
                        Con số bám tồn kho thật (cột "Tiền hao hụt") là con số nên dùng để truy
                        thất thoát từng NVL; ô đầu màn giữ nguyên để đối chiếu với mảng mua hàng.
                      </div>
                    </div>
                  )}

                  <p className="small text-muted mb-0">
                    NVL mất NHIỀU TIỀN NHẤT xếp lên đầu — bấm tiêu đề <strong>Tiền hao hụt</strong> để
                    đổi chiều. <strong>Tiền hao hụt</strong> là đúng con số ở dòng "⇒ THIẾU … ≈ …đ"
                    trong phần mở rộng của chính dòng đó (số lượng lệch × giá vốn); dòng nhỏ bên dưới
                    là phần trăm khoản đó chiếm trong <strong>tổng tiền hao hụt</strong> của kỳ —
                    mẫu số là dòng Tổng ở cuối bảng, cộng cột này ra đúng 100%. NVL <strong>dư</strong>{' '}
                    (dùng ít hơn định mức) ghi "dư …đ", không tô đỏ và không có phần trăm.{' '}
                    <strong>% hao hụt</strong> ={' '}
                    (tồn đầu kỳ + nhập trong kỳ − lượng theo định mức − chuyển kho ra − tồn cuối kỳ) ÷
                    (tồn đầu kỳ + nhập trong kỳ); <strong>Số lượng lệch</strong> là chính phần tử số đó,
                    kèm đơn vị. Ô hiện "—" nghĩa là mẫu số bằng 0, mốc tồn kho của NVL đó chưa dùng
                    được, hoặc NVL chưa có giá vốn nên không quy ra tiền được — lý do luôn được ghi
                    bằng chữ nhỏ ngay dưới <strong>tên NVL</strong>. Bảng này chỉ
                    gồm NVL <strong>CÓ công thức</strong>; vật tư vận hành (ống hút, túi rác…) nằm ở cuối
                    phần <strong>Xem số liệu chi tiết</strong> và không tính vào con số hao hụt nào. Bấm
                    mũi tên đầu dòng để xem phép tính hao hụt của từng NVL đọc từ trên xuống, kèm công tắc bật
                    đếm tay mỗi ca; số theo tiền và số theo đơn vị gốc nằm trong khối{' '}
                    <strong>Số liệu tiền và số liệu gốc</strong> ngay trong đó.
                  </p>
                </>
              )}
            </Card.Body>
          </Card>

          {/* #172026: TOÀN BỘ phần còn lại của màn cũ nằm sau nút này — không xoá số liệu nào, chỉ
              không hiện mặc định. Thứ tự: cảnh báo dữ liệu → công thức cũ → biểu đồ → nhóm cùng đơn
              vị → tiền → đối chiếu đếm tay → vật tư vận hành (cuối cùng). */}
          <div className="my-3">
            <Button variant="outline-secondary" size="sm" onClick={() => setShowDetails((v) => !v)}>
              {showDetails ? <ChevronDown size={16} className="me-1" /> : <ChevronRight size={16} className="me-1" />}
              Xem số liệu chi tiết
            </Button>
          </div>

          {showDetails && (
            <>
              {dqCount > 0 && (
                <Card className="mb-3 border-warning">
                  <Card.Body>
                    <Button
                      variant="link"
                      className="p-0 text-decoration-none text-warning d-flex align-items-center gap-2 w-100 text-start"
                      onClick={() => setShowDataQuality((v) => !v)}
                      aria-expanded={showDataQuality}
                    >
                      {showDataQuality ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <AlertTriangle size={18} />
                      <span className="fw-medium">
                        Dữ liệu chưa sẵn sàng ({dqCount}) — số hao hụt bên dưới có thể chưa chính xác
                        {showDataQuality ? '' : ' — bấm để xem'}
                      </span>
                    </Button>

                    {showDataQuality && (
                    <div className="mt-3">

                    {dq.menu_items_without_recipe.length > 0 && (
                      <div className="mb-2">
                        <div className="fw-medium small">Món bán ra chưa có công thức NVL:</div>
                        <ul className="small mb-0">
                          {dq.menu_items_without_recipe.map((m) => (
                            <li key={m.menu_item_id}>
                              {m.name} — {m.qty_sold} món, {fmtCurrency(m.revenue)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {dq.materials_without_price.length > 0 && (
                      <div className="mb-2">
                        <div className="fw-medium small">NVL tiêu thụ nhưng chưa có giá vốn ở bất kỳ nguồn nào (không tính được tiền):</div>
                        <ul className="small mb-0">
                          {dq.materials_without_price.map((m) => (
                            <li key={m.material_id}>
                              {m.name} — đã dùng {Number(m.qty_consumed).toLocaleString('vi-VN')} {m.unit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {dq.btp_data_issues.length > 0 && (
                      <div className="mb-2">
                        <div className="fw-medium small">Bán thành phẩm (BTP) cấu hình chưa đúng:</div>
                        <ul className="small mb-0">
                          {dq.btp_data_issues.map((b) => (
                            <li key={b.material_id}>
                              {b.name} — {btpReasonLabel(b.reason)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {dq.base_unit_package_purchases.length > 0 && (
                      <div className="mb-2">
                        <div className="fw-medium small">NVL từng nhập qua gói đơn vị cơ sở (nghi ngờ chọn nhầm quy cách đóng gói khi tạo đơn đặt hàng):</div>
                        <ul className="small mb-0">
                          {dq.base_unit_package_purchases.map((b) => (
                            <li key={b.material_id}>
                              {b.name} — {fmtCurrency(b.cost_per_base)}/{b.unit} (đơn {b.example_order_number})
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {dq.suspicious_purchase_pricing.length > 0 && (
                      <div className="mb-2">
                        <div className="fw-medium small">NVL có giá nhập chênh lệch bất thường giữa các lần mua:</div>
                        <ul className="small mb-0">
                          {dq.suspicious_purchase_pricing.map((s) => (
                            <li key={s.material_id}>
                              {s.name} — từ {fmtCurrency(s.min_cost)} đến {fmtCurrency(s.max_cost)} (đơn {s.max_cost_order_number})
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {dq.orders_missing_deduction.count > 0 && (
                      <div className="mb-2">
                        <div className="fw-medium small">
                          {dq.orders_missing_deduction.count} đơn có món đã có công thức nhưng hệ thống chưa trừ kho tự động (có thể do lỗi vận hành lúc order hoàn tất).
                        </div>
                      </div>
                    )}

                    {/* #153458 (PA2): 3 nhóm cảnh báo mới, chỉ có ý nghĩa từ khi công thức đọc tồn kho. */}
                    {dq.unit_scale_stock_adjustments.length > 0 && (
                      <div className="mb-2">
                        <div className="fw-medium small">
                          Bút toán kho lệch đúng khoảng 1000× (gõ nhầm g↔kg, ml↔L) — KHÔNG phải hao hụt thật,
                          các NVL này đã bị loại khỏi những con số hao hụt phía trên:
                        </div>
                        <ul className="small mb-0">
                          {dq.unit_scale_stock_adjustments.map((a) => (
                            <li key={`${a.material_id}-${a.at}`}>
                              {a.name} — tồn {Number(a.balance_before).toLocaleString('vi-VN')} →{' '}
                              {Number(a.balance_after).toLocaleString('vi-VN')} ngày {fmtDate(a.at)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {dq.materials_without_opening_baseline.length > 0 && (
                      <div className="mb-2">
                        <div className="fw-medium small">
                          NVL không có giao dịch kho nào trước ngày đầu kỳ — tồn đầu kỳ đang coi là 0, đây là
                          suy ra chứ không đo được, nên số hao hụt của các NVL này có thể âm giả:
                        </div>
                        <ul className="small mb-0">
                          {dq.materials_without_opening_baseline.map((m) => (
                            <li key={m.material_id}>{m.name}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {dq.backdated_stock_transactions.count > 0 && (
                      <div className="mb-0">
                        <div className="fw-medium small">
                          {dq.backdated_stock_transactions.count} giao dịch kho có số dư không nối tiếp theo
                          thứ tự thời gian (dấu hiệu ghi lùi ngày) — mốc tồn của các NVL sau kém tin cậy:{' '}
                          {dq.backdated_stock_transactions.materials.map((m) => m.name).join(', ')}.
                        </div>
                      </div>
                    )}
                    </div>
                    )}
                  </Card.Body>
                </Card>
              )}

              {/* #153458: con số của CÔNG THỨC CŨ trên đúng kỳ đang xem — giữ để đối chiếu, nhưng
                  #172026 đưa xuống đây vì nó không giúp trả lời "NVL nào đang mất bất thường". */}
              <Alert variant="light" className="border small py-2 mb-3">
                {/* #172026 bổ sung 2: trung vị % của công thức MỚI lui từ cục chính xuống đây —
                    nó trộn NVL khác đơn vị nên không dùng để ra quyết định, nhưng vẫn giữ để đối
                    chiếu giữa hai công thức. */}
                Công thức MỚI (tồn đầu/cuối kỳ) trên kỳ này:{' '}
                <strong>{fmtPct(balanceQty?.median_shrinkage_pct ?? null)}</strong> trung vị %
                giữa các NVL (chỉ để tham khảo — các NVL khác đơn vị đo nên con số này không đại
                diện cho mặt hàng nào).
                <br />
                Công thức CŨ (tiền mua − tiền lý thuyết) trên cùng kỳ này:{' '}
                <strong>{fmtPct(qty?.median_variance_pct ?? null)}</strong> trung vị GIỮA CÁC NVL ·{' '}
                <strong>{qty ? `${qty.materials_over_threshold}/${qty.materials_measurable}` : '—'}</strong>{' '}
                NVL vượt ngưỡng.
              </Alert>

              <Card className="mb-3">
                <Card.Body>
                  <Card.Title className="h6 mb-2">
                    % hao hụt số lượng theo thời gian — công thức mới so với công thức cũ
                  </Card.Title>
                  <Form.Group controlId="shrinkage-granularity" className="mb-2" style={{ maxWidth: 260 }}>
                    <Form.Label className="small mb-1">Độ chi tiết biểu đồ</Form.Label>
                    <Form.Select size="sm" value={granularity} onChange={(e) => setGranularity(e.target.value)}>
                      <option value="day">Theo ngày</option>
                      <option value="week">Theo tuần</option>
                      <option value="month">Theo tháng</option>
                    </Form.Select>
                  </Form.Group>
                  {granularityWarning && (
                    <Alert variant="warning" className="small py-2 d-flex align-items-start gap-2">
                      <AlertTriangle size={16} className="flex-shrink-0 mt-1" />
                      <span>
                        Đang xem <strong>{granularity === 'day' ? 'theo ngày' : 'theo tuần'}</strong> — số hao hụt
                        ở biểu đồ có thể <strong>cao giả</strong>. Hàng nhập về theo lô: ngày nào có lô hàng thì
                        ngày đó "mua nhiều hơn bán" nên hao hụt vọt lên, dù thực tế không mất gì. Muốn xem con số
                        đáng tin, hãy chọn <strong>Theo tháng</strong>.
                      </span>
                    </Alert>
                  )}
                  <ShrinkageChartComponent data={qtyChartData} mode="qty" />
                </Card.Body>
              </Card>

              {/* #172026 bổ sung 2 — ĐÃ GỠ HẲN bảng "Hao hụt số lượng theo nhóm cùng đơn vị"
                  (kể cả khỏi phần chi tiết này). Sếp hỏi "cái này ai yêu cầu, làm để làm gì?" và
                  NĐH nhận yêu cầu #142018 là SAI: gộp 9 NVL khác nhau chỉ vì cùng đơn vị "G" không
                  nói lên điều gì, "GOI · 1 NVL · +681,3%" là số rác. Ý định ban đầu chỉ là CẤM cộng
                  bừa số lượng giữa các NVL khác đơn vị — nguyên tắc đó vẫn giữ, nhưng thể hiện
                  đúng chỗ: mỗi NVL có % riêng của chính nó ở bảng 4 cột bên trên. Dữ liệu
                  `qty_summary.by_unit` phía backend KHÔNG bị xoá, chỉ không hiển thị nữa. */}

              {/* #192444: biểu đồ "Tiền mua vs tiền lý thuyết theo thời gian" ĐÃ CHUYỂN LÊN ĐẦU
                  MÀN, nằm ngay dưới 3 cục tiền — xem khối ở đầu component. Không nhân bản lại ở
                  đây. */}

              {/* #153458 (PA3): đối chiếu số hệ thống với SỐ ĐẾM TAY của nhân viên từng ca. Mục đích
                  duy nhất là cho thấy chỗ nào hai nguồn KHÔNG khớp nhau. */}
              {shiftSummary && (
                <Card className="mb-3">
                  <Card.Body>
                    <Card.Title className="h6 mb-1">Đối chiếu số đếm tay theo ca</Card.Title>
                    <p className="small text-muted mb-3">
                      So số <strong>hệ thống</strong> tin là còn với số <strong>nhân viên đếm tay</strong> ở
                      đầu/cuối ca. Chỉ những NVL được gắn cờ kiểm ca mới có số đếm tay —{' '}
                      <strong>{shiftSummary.materials_with_count}</strong> NVL có số,{' '}
                      <strong>{shiftSummary.materials_flagged_without_count}</strong> NVL có cờ nhưng kỳ này
                      chưa có số dùng được, <strong>{shiftSummary.materials_not_flagged}</strong> NVL chưa bật
                      kiểm ca nên không có gì để đối chiếu.
                      {shiftSummary.excluded_unit_scale_count > 0 && (
                        <>
                          {' '}Đã loại <strong>{shiftSummary.excluded_unit_scale_count}</strong> dòng kiểm lệch
                          đúng khoảng 1000× (gõ nhầm g↔kg, ml↔L) — đó là lỗi nhập liệu, không phải hao hụt.
                        </>
                      )}
                    </p>
                    <Table bordered size="sm" responsive hover className="mb-2">
                      <thead>
                        <tr>
                          <th>NVL</th>
                          <th className="text-end">Số lần kiểm</th>
                          <th className="text-end">Số lần 2 nguồn lệch nhau</th>
                          <th className="text-end">Lệch điển hình (chỉ các lần lệch)</th>
                          <th className="text-end">% lệch lớn nhất</th>
                          <th className="text-end">Dòng loại vì lỗi đơn vị</th>
                          <th>Lần kiểm gần nhất</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shiftRows.map((m) => {
                          const sc = m.shift_count;
                          const hasGap = sc.checks_with_variance > 0;
                          return (
                            <tr key={m.material_id}>
                              <td>{m.name}</td>
                              <td className="text-end">{sc.checks_count}</td>
                              <td className={`text-end ${hasGap ? 'text-danger fw-medium' : 'text-success fw-medium'}`}>
                                {sc.status === 'HAS_COUNT' ? sc.checks_with_variance : '—'}
                              </td>
                              <td className="text-end">
                                {sc.status === 'HAS_COUNT' && sc.checks_with_variance === 0
                                  ? <span className="text-success">khớp toàn bộ</span>
                                  : fmtPct(sc.typical_variance_pct)}
                              </td>
                              <td className="text-end">
                                {sc.status === 'HAS_COUNT' && sc.checks_with_variance === 0
                                  ? <span className="text-success">khớp toàn bộ</span>
                                  : fmtPct(sc.max_abs_variance_pct)}
                              </td>
                              <td className="text-end">{sc.excluded_unit_scale_count || '—'}</td>
                              <td className="small text-muted">
                                {sc.status === 'HAS_COUNT'
                                  ? fmtDate(sc.last_checked_at)
                                  : SHIFT_STATUS_LABEL[sc.status]}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    <p className="small text-muted mb-0">
                      Cột <strong>Lệch điển hình</strong> là trung vị % tính CHỈ TRÊN các lần đếm có lệch —
                      các lần đếm khớp không được đưa vào, vì trước đây chúng kéo con số này về +0,0% ngay
                      cả khi có hàng chục lần lệch thật (#172543). NVL nào không lần nào lệch thì ghi thẳng
                      "khớp toàn bộ" thay vì hiện 0%. Bảng đọc theo <strong>số lần</strong> và{' '}
                      <strong>mức lệch %</strong>, cố ý KHÔNG có cột
                      tổng số lượng lệch: trên dữ liệu thật, số hệ thống của vài NVL đã bị lỗi gõ nhầm đơn vị
                      kéo lệch (có lần hệ thống tin là còn 777.000g trong khi thực tế khoảng 1.000g), nên một
                      con số tổng sẽ trông chính xác mà sai hẳn. Muốn thêm NVL vào diện đếm tay thì bật cờ{' '}
                      <strong>Kiểm ca</strong> ở phần mở rộng của NVL đó trong bảng "Chi tiết theo NVL" — mỗi
                      NVL bật thêm là thêm việc đếm cho nhân viên mỗi ca, nên cân nhắc trước khi bật.
                    </p>
                  </Card.Body>
                </Card>
              )}

              {/* #151411: vật tư vận hành KHÔNG nằm trong công thức món nào — cố ý chỉ hiện tổng tiền
                  mua, không có % và không gắn nhãn "hao hụt", và đã bị loại khỏi mọi cục tổng ở trên.
                  #172026: dời xuống CUỐI phần chi tiết. */}
              {suppliesSummary && suppliesSummary.materials_count > 0 && (
                <Card>
                  <Card.Body>
                    <Card.Title className="h6 mb-1">
                      Vật tư vận hành — không nằm trong công thức món ({suppliesSummary.materials_count} loại)
                    </Card.Title>
                    <p className="small text-muted mb-3">
                      Đây là hàng mua để vận hành quán (ống hút, túi rác, ly, giấy…), không đi vào món nào
                      nên <strong>không có "lượng lý thuyết cần dùng" để so</strong>. Vì vậy chỉ hiện tổng
                      tiền mua — <strong>không phải hao hụt</strong>, và đã được loại khỏi các con số hao
                      hụt phía trên.
                    </p>
                    <Table bordered size="sm" responsive hover className="mb-2">
                      <thead>
                        <tr>
                          <th>Vật tư</th>
                          <th className="text-end">SL mua vào</th>
                          <th className="text-end">Tiền mua</th>
                        </tr>
                      </thead>
                      <tbody>
                        {supplies.map((sp) => {
                          const mat = materialsById.get(sp.material_id) ?? { unit: sp.unit };
                          return (
                            <tr key={sp.material_id}>
                              <td>{sp.name}</td>
                              <td className="text-end">
                                {sp.purchase_qty > 0
                                  ? <QtyDual quantityInBase={sp.purchase_qty} material={mat} />
                                  : '—'}
                              </td>
                              <td className="text-end">{fmtCurrency(sp.purchase_value)}</td>
                            </tr>
                          );
                        })}
                        <tr className="table-light fw-medium">
                          <td>Tổng tiền mua vật tư vận hành</td>
                          <td />
                          <td className="text-end">{fmtCurrency(suppliesSummary.purchase_value)}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
